import { SearchUserQueryString } from '@/app/shared/filter';
import { Component, ElementRef, HostListener, QueryList, ViewChildren } from '@angular/core';
import { CommonService } from '../../services/common.service';
import { QueryStringService } from '../../services/querystring.service';
import { map, Subject, Subscription, takeUntil } from 'rxjs';
import { CatetoryQueryString, LabelQueryString, ManufacturerQueryString, PageQueryString } from '../../filter';
import { CategoryService } from '../../services/category.service';
import { hasProductMenuRequest, isBasicProductGroup, isRecentProductsMenu } from '../../interface/category.interface';

@Component({
  selector: 'app-category-filter',
  templateUrl: './category-filter.component.html',
  styleUrl: './category-filter.component.scss'
})
export class CategoryFilterComponent {
  typeMap = {
    'categories': 'category',
    'labels': 'label',
  }
  SPECIAL_SLUG = {
    ALL: 'tat-ca-san-pham',
    MANUFACTURER: 'manufacturer',
  }
  GROUP_TYPE = {
    CATEGORY: 0, // This type is apply in case category and label
    FILTER: 1, // This type is apply in case manufacturer, ingredient and pathology
  }

  public categories: any[] = [];
  public originConfigCategories: any[] = [];
  public categoryQuery: CatetoryQueryString;
  public labelQuery: LabelQueryString;
  private changeQueryStringSubcription?: Subscription;
  private updatedFromView = false;
  isShowAllTags = true;
  private destroy$ = new Subject<void>();
  private manufacturerRequest?: Subscription;

  @ViewChildren('categoryPopup', { read: ElementRef })
  categoryPopup!: QueryList<ElementRef<HTMLElement>>;

  constructor(
    private commonService: CommonService,
    private queryStringService: QueryStringService,
    private categoryService: CategoryService,
  ) {
    this.changeQueryStringSubcription = this.queryStringService.eventChangeQueryString.subscribe(() => {
      this.categoryQuery = this.queryStringService.getQueryString(new CatetoryQueryString());

      this.labelQuery = this.queryStringService.getQueryString(new LabelQueryString());
      if (!this.updatedFromView) {
        this.updateSelectedCategory(this.categories);
      } else {
        this.updatedFromView = false;
      }
    });
  }

  ngOnInit(): void {
    const savedValue = sessionStorage.getItem('isShowAllTags');

    if (savedValue !== null) {
      this.isShowAllTags = savedValue === 'true';
    }
    this.categoryQuery = this.queryStringService.getQueryString(new CatetoryQueryString());
    this.labelQuery = this.queryStringService.getQueryString(new LabelQueryString());
    this.getConfigCategories();
  }

  mapMenuWithProductCate(data: any[]): any {
    return data.filter(item => item.visible !== false).map((item, index) => ({
      id: index,
      name: item.name,
      children: item.children ? this.mapMenuWithProductCate(item.children) : undefined,
      visible: item.visible,
      query: item.query,
      method: item.method,
      url: item.url,
      type: this.getType(item),
      slug: item.slug,
      color: item.borderColor,
      icon: `assets/svg/category/${item.icon || 'all'}.svg`,
      activeIcon: `assets/svg/category/${item.icon || 'all'}_active.svg`,
      hoverIcon: `assets/svg/category/${item.icon || 'all'}_hover.svg`,
    }));
  }

  getType(item: any): string {
    if (hasProductMenuRequest(item)) return 'category';
    const q = item.query || {}
    const key = Object.keys(this.typeMap).find(k => Array.isArray(q[k]))
    return key ? this.typeMap[key as keyof typeof this.typeMap] : ''
  }

  getConfigCategories() {
    this.commonService.getCategoriesV2.pipe(takeUntil(this.destroy$)).subscribe({
      next: (data: any) => {
        if (data) {
          this.originConfigCategories = data;
          this.categories = this.mapMenuWithProductCate(data.filter(isBasicProductGroup));
          this.updateSelectedCategory(this.categories);
          if (this.categoryQuery.value) {
            const selectedCate = this.findCategoryBySlug(this.categories, this.categoryQuery.value);
            if (selectedCate) {
              if (hasProductMenuRequest(selectedCate) && !isRecentProductsMenu(selectedCate)) {
                this.commonService.setManufacturerCategoriesV2([]);
              } else {
                this.getManuCategories(selectedCate.query, isRecentProductsMenu(selectedCate));
              }
            }
          }
        }
      },
    });
  }

  findCategoryBySlug(items: any[], slug: string): any | null {
    for (const item of items) {
      if (item.slug && item.slug.toLowerCase() === slug?.toLowerCase()) {
        return item;
      }

      if (item.children?.length) {
        const found = this.findCategoryBySlug(item.children, slug);

        if (found) {
          return found;
        }
      }
    }

    return null;
  }

  getManuCategories(body: any, recentProducts = false) {
    this.manufacturerRequest?.unsubscribe();
    const request = recentProducts ? this.categoryService.getRecentProductManufacturers()
      : this.categoryService.getManuCategories(body).pipe(map(data => data?.items ?? []));
    this.manufacturerRequest = request.pipe(takeUntil(this.destroy$)).subscribe({
      next: items => this.commonService.setManufacturerCategoriesV2(items),
      error: () => this.commonService.setManufacturerCategoriesV2([]),
    })
  }

  updateSelectedCategory(
    cat: any[] | undefined,
    parent: any | undefined = undefined,
    queryStringValue: {
      category: string[],
      label: string[],
    } | undefined = undefined,
  ) {
    if (typeof queryStringValue === 'undefined') {
      // Lowercase: so với c.slug không phân biệt hoa thường (URL canonical là lowercase).
      queryStringValue = {
        category: this.categoryQuery.originalValue.map((slug: string) => slug.toLowerCase()),
        label: this.labelQuery.originalValue.map((slug: string) => slug.toLowerCase()),
      };
    }
    if (!cat || cat.length === 0) {
      return;
    }
    if ((
      this.categoryQuery.originalValue.length === 0 && this.labelQuery.originalValue.length === 0)
      || queryStringValue.category.includes(this.SPECIAL_SLUG.ALL))
    {
      for (const c of cat) {
        c.active = !c.type && c.slug == this.SPECIAL_SLUG.ALL;
        this.activeRecursive(this.SPECIAL_SLUG.ALL, this.categories);
        this.commonService.setManufacturerCategoriesV2(this.originConfigCategories.find((c: any) => c.slug === this.SPECIAL_SLUG.MANUFACTURER)?.children ?? []);
      }
      return;
    }
    if (cat && cat.length) {
      for (const c of cat) {
        switch (c.type) {
          case '':
            c.active = false;
            break;
          case 'category':
            let indCate = queryStringValue.category.indexOf(c.slug?.toLowerCase());
            if (indCate >= 0) {
              c.active = true;
              queryStringValue.category.splice(indCate, 1);
            } else {
              c.active = false;
            }
            break;
          case 'label':
            let indLabel = queryStringValue.label.indexOf(c.slug?.toLowerCase());
            if (indLabel >= 0) {
              c.active = true;
              queryStringValue.label.splice(indLabel, 1);
            } else {
              c.active = false;
            }
            break;
        }
        this.updateSelectedCategory(c.children, c, queryStringValue);
      }
      if (parent) {
        parent.childrenSelected = cat.some(p => p.active || p.childrenSelected);
      }
    }
  }

  selecteCate(category: any) {
    if (category.active) {
      return;
    }
    this.manufacturerRequest?.unsubscribe();
    this.activeRecursive(category.slug, this.categories);
    this.updatedFromView = true;
    if (category.slug == this.SPECIAL_SLUG.ALL) {
      this.commonService.setManufacturerCategoriesV2(this.originConfigCategories.find((c: any) => c.slug === this.SPECIAL_SLUG.MANUFACTURER)?.children ?? []);
    } else if (hasProductMenuRequest(category) && !isRecentProductsMenu(category)) {
      this.commonService.setManufacturerCategoriesV2([]);
    } else {
      this.getManuCategories(category.query, isRecentProductsMenu(category));
    }
    this.selectRecursive(this.categories);
    this.changeQueryString(category.type);
  }

  activeRecursive(slug: string, items: any[], parent: any = null) {
    for (const c of items) {
      c.active = slug === c.slug;
      c.selected = false;
      c.childrenSelected = false;
      if (c.children?.length) {
        this.activeRecursive(slug, c.children, c)
      }
      if (parent) {
        parent.childrenSelected = items.some((p:any) => p.active || p.childrenSelected);
      }
    }
  }

  selectRecursive(categories: any[], parent: any | undefined = undefined) {
    if (categories.length) {
      for (const cat of categories) {
        if (cat.children && cat.children.length) {
          if (parent) {
            parent.childrenSelected = cat.children.some((p: any) => p.active);
          }
          this.selectRecursive(cat.children, cat);
        }
      }
    }
  }

  changeQueryString(type: string) {
    if (!type) {
      this.queryStringService.changeQueryString([new CatetoryQueryString(), new PageQueryString(), new LabelQueryString()]);
      return
    }
    let s = this.getQueryString(this.categories);
    switch (type) {
      case 'category':
        this.categoryQuery.value = s.strQueryCate;
        this.queryStringService.changeQueryString([this.categoryQuery, new LabelQueryString(), new ManufacturerQueryString(), new PageQueryString(), new SearchUserQueryString()]);
        break;
      case 'label':
        this.labelQuery.value = s.strQueryLabel;
        this.queryStringService.changeQueryString([this.labelQuery, new CatetoryQueryString(), new ManufacturerQueryString(), new PageQueryString(), new SearchUserQueryString()]);
        break;
    }
  }

  getQueryString(cat: any[] | undefined) {
    let strQueryCate = '';
    let strQueryLabel = '';
    if (!cat || cat.length === 0) {
      return { strQueryCate, strQueryLabel };
    }
    for (const c of cat) {
      if (c.active) {
        switch (c.type) {
          case 'category':
            strQueryCate += `${c.slug},`
            break;
          case 'label':
            strQueryLabel += `${c.slug},`
            break;
        }
      }
      const {
        strQueryCate: childStrQueryCate,
        strQueryLabel: childStrQueryLabel,
      } = this.getQueryString(c.children);
      strQueryCate += childStrQueryCate;
      strQueryLabel += childStrQueryLabel;
    }
    return { strQueryCate, strQueryLabel };
  }

  showChildren(category: any, event: MouseEvent, siblings?: any[]) {
    if (window.innerWidth <= 1024) {
      event.stopPropagation();

      category.selected = !category.selected;

      if (!category.selected) {
        category.popupStyle = null;
        return;
      }

      if (siblings && siblings.length) {
        siblings.forEach(sibling => {
          if (sibling !== category) {
            sibling.selected = false;
            sibling.popupStyle = null;
          }
        });
      }

      const icon = event.currentTarget as HTMLElement;
      const tag = icon.closest('.category-tag') as HTMLElement;

      if (!tag) return;

      const rect = tag.getBoundingClientRect();

      const menuWidth = 250;
      const screenPadding = 8;

      let left = rect.left;
      const top = rect.bottom + 4;

      // Nếu popup bị tràn sang phải thì kéo ngược lại
      if (left + menuWidth > window.innerWidth - screenPadding) {
        left = window.innerWidth - menuWidth - screenPadding;
      }

      // Không cho tràn khỏi mép trái
      if (left < screenPadding) {
        left = screenPadding;
      }

      category.popupStyle = {
        top: `${top}px`,
        left: `${left}px`,
        width: `${menuWidth}px`
      };
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const clickTarget = event.target as Node;
    if (this.categoryPopup?.some(root => root.nativeElement.contains(clickTarget))) {
      return;
    }

    this.closeAllSubmenus(this.categories);
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(event: Event) {
    this.closeAllSubmenus(this.categories);
  }

  closeAllSubmenus(categories: any[]) {
    categories.forEach(category => {
      category.selected = false;
      if (category.children?.length) {
        this.closeAllSubmenus(category.children);
      }
    });
  }

  checkHoverFollowScreenSize(isHover: boolean) {
    if (window.innerWidth <= 992) {
      return false;
    }
    return isHover;
  }

  toggleShowTags() {
    this.isShowAllTags = !this.isShowAllTags;
    sessionStorage.setItem(
      'isShowAllTags',
      String(this.isShowAllTags)
    );
  }

  ngOnDestroy(): void {
    this.changeQueryStringSubcription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
